import type { Dispatch, SetStateAction } from "react";
import type { SavingInstitution, SavingPocket } from "@/hooks/useSavingPockets";
import type { Transaction } from "@/hooks/useTransactions";
import { parseDateLocal, toLocalYMD } from "@/utils/dateUtils";
import type {
  BudgetInsightLike,
  BudgetStatus,
} from "@/domains/analytics/utils/budgetInsights";

export type CategoryInsight = BudgetInsightLike & {
  budgetId?: number;
  budgetDescription?: string | null;
};

type HiddenPieItems = Array<number | string>;

export const toggleHiddenItem = (
  itemId: number | string,
  hiddenItems: HiddenPieItems,
  setHiddenItems: Dispatch<SetStateAction<HiddenPieItems>>,
) => {
  setHiddenItems(
    hiddenItems.includes(itemId)
      ? hiddenItems.filter((id) => id !== itemId)
      : [...hiddenItems, itemId],
  );
};

export const getBudgetMeta = (status: BudgetStatus) => {
  if (status === "over") {
    return {
      accent: "var(--app-danger)",
      track: "var(--app-danger-chip-bg)",
      borderClass: "border-[var(--app-danger-border)]",
      chipClass: "bg-[var(--app-danger-chip-bg)] text-[var(--app-danger)]",
      labelClass: "text-[var(--app-danger)]",
    };
  }

  if (status === "near") {
    return {
      accent: "var(--app-warning)",
      track: "var(--app-warning-chip-bg)",
      borderClass: "border-[var(--app-warning-border)]",
      chipClass: "bg-[var(--app-warning-chip-bg)] text-[var(--app-warning)]",
      labelClass: "text-[var(--app-warning)]",
    };
  }

  if (status === "safe") {
    return {
      accent: "var(--app-success)",
      track: "var(--app-success-chip-bg)",
      borderClass: "border-[var(--app-success-border)]",
      chipClass: "bg-[var(--app-success-chip-bg)] text-[var(--app-success-text)]",
      labelClass: "text-[var(--app-success-text)]",
    };
  }

  return {
    accent: "var(--app-text-secondary)",
    track: "var(--app-neutral-chip-bg)",
    borderClass: "border-[var(--app-border)]",
    chipClass: "bg-[var(--app-neutral-chip-bg)] text-[var(--app-text-secondary)]",
    labelClass: "text-[var(--app-text-secondary)]",
  };
};

export const getProgressWidth = (category: Pick<CategoryInsight, "usagePercent">) =>
  `${Math.min(100, Math.max(0, (category.usagePercent ?? 0) * 100))}%`;

export const isIncompleteInstallment = (installment: {
  active: boolean;
  amount_left: number;
  completed?: boolean | null;
}) =>
  !installment.completed &&
  installment.active &&
  Number(installment.amount_left ?? 0) > 0;

export const buildPieData = <T>(
  items: T[],
  getId: (item: T) => number | string,
  getName: (item: T) => string,
  getValue: (item: T) => number,
) =>
  items
    .map((item) => ({
      id: getId(item),
      name: getName(item),
      value: Number(getValue(item) ?? 0),
    }))
    .filter((item) => item.value > 0)
    .sort((a, b) => b.value - a.value);

export const buildSavingsDistributionData = (
  pockets: SavingPocket[],
  institutions: SavingInstitution[],
  unknownName: string,
) => {
  const institutionNames = new Map(
    institutions.map((institution) => [institution.id, institution.name]),
  );
  const totals = new Map<number, { id: number; name: string; value: number }>();

  pockets.forEach((pocket) => {
    const current = totals.get(pocket.saving_institution_id);
    if (current) {
      current.value += Number(pocket.amount ?? 0);
      return;
    }
    totals.set(pocket.saving_institution_id, {
      id: pocket.saving_institution_id,
      name: institutionNames.get(pocket.saving_institution_id) ?? unknownName,
      value: Number(pocket.amount ?? 0),
    });
  });

  return [...totals.values()]
    .filter((item) => item.value > 0)
    .sort((a, b) => b.value - a.value);
};

export const buildCategoryTransactionBreakdown = (
  transactions: Transaction[],
  expenseTypeId: number | undefined,
) => {
  const normalizedTransactions = transactions
    .map((transaction) => {
      const parsedDate = parseDateLocal(transaction.transaction_date);
      return {
        ...transaction,
        amount: Math.abs(Number(transaction.amount ?? 0)),
        parsedDate,
        dateKey: toLocalYMD(parsedDate),
      };
    })
    .filter(
      (transaction) =>
        transaction.type_id === expenseTypeId &&
        transaction.amount > 0 &&
        transaction.dateKey &&
        !Number.isNaN(transaction.parsedDate.getTime()),
    )
    .sort((a, b) => b.amount - a.amount);
  const totalDetailedSpend = normalizedTransactions.reduce(
    (sum, transaction) => sum + transaction.amount,
    0,
  );
  const topTransactions = normalizedTransactions.slice(0, 3);
  const topThreeTotal = topTransactions.reduce(
    (sum, transaction) => sum + transaction.amount,
    0,
  );

  return {
    transactions: normalizedTransactions,
    transactionCount: normalizedTransactions.length,
    totalDetailedSpend,
    topTransactions,
    topThreeTotal,
    topThreeShare: totalDetailedSpend > 0 ? topThreeTotal / totalDetailedSpend : 0,
    largestAmount: normalizedTransactions[0]?.amount ?? 0,
  };
};
